import React, {useEffect, useState} from "react";
import {Breadcrumb, Button, Input, InputNumber, Layout, message, Popconfirm, Space, Table, Tabs, Tag} from 'antd';
import {Link} from "react-router-dom"
import Api from "../api";
import {CheckCircleOutlined, CheckOutlined, DeleteOutlined, RobotOutlined} from '@ant-design/icons';
import NewBarcodeIngredient from "../components/new-barcode-ingredient";
import NewIngredient from "../components/new-ingredient";
import axios from "axios";

const {TabPane} = Tabs;
const {Search} = Input;
const {Content} = Layout;

const Ingredients = () => {
    const [ingredients, setIngredients] = useState([]);
    const [active, setActive] = useState('fitlap');
    const [meta, setMeta] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [search, setSearch] = useState("");
    const [editingScore, setEditingScore] = useState({});
    const idIndex = active === 'history' ? 'ingredient_id' : 'id';

    useEffect(() => {
        loadIngredients();
    }, [active]);

    // Save dummy score
    const saveIngredientScore = (id, score) => {
        if (score > 10) {
            return message.error('max score 10');
        }

        Api.put("/admin/ingredients/" + id, {score: score}).then(res => {
            console.log(res);
            return message.success('Score saved!');
        }).catch(err => {
            return message.error('Ingredient update failed');
        })

        setIngredients(prev =>
            prev.map(item => (item.id === id ? {...item, score} : item))
        );

        setEditingScore({});
    };

    const queryChatGPT = (message, OpenAIApiKey, model = 'gpt-3.5-turbo') => {
        console.log(message);
        return axios.post(
            'https://api.openai.com/v1/chat/completions',
            {
                model: model,
                messages: [
                    {role: 'system', content: 'You are a helpful assistant.'},
                    {role: 'user', content: message}
                ],
                temperature: 0.7,
                max_tokens: 1000
            },
            {
                headers: {
                    Authorization: `Bearer ${OpenAIApiKey}`,
                    'Content-Type': 'application/json'
                }
            }
        ).then(response => {
            return response.data.choices[0].message.content; // The generated prompt
        }).catch(error => {
            console.error('Error querying ChatGPT:', error);
            throw error;
        });
    };


    // AI generate scores demo
    const generateIngredientScores = () => {
        const OpenAIApiKey = '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'; // your key
        const formattedIngredients = ingredients.map(i => i.original_name).join(', ');

        message.loading('Generating scores with AI...', 2);

        // Example dummy request to ChatGPT
        queryChatGPT(
            `Give a healthiness score from ${active === 'fitlap' ? 6 : 1}-10 for each ingredient: ${formattedIngredients}. Return JSON with ingredient -> score.`,
            OpenAIApiKey
        )
            .then(response => {
                parseAIScores(response);
            })
            .catch(() => {
                message.error("Failed to generate scores");
            });
    };

    const parseAIScores = (aiResponse) => {
        try {
            // Remove ```json or ``` and trim whitespace
            const clean = aiResponse
                .replace(/```json/, '')
                .replace(/```/, '')
                .trim();

            const scores = JSON.parse(clean);

            const updatedIngredients = ingredients.map(i => {
                const score = scores[i.original_name] ?? scores[i.name];
                return {...i, score: score !== undefined ? score : i.score ?? null};
            });

            setIngredients(updatedIngredients);
            message.success("AI scores applied!");
        } catch (e) {
            console.error(e);
            message.error("AI returned invalid JSON");
        }
    };

    const columns = [
        {
            title: 'ID/verified',
            dataIndex: 'id',
            key: 'id',
            width: 30,
            render: (verified, record) => <Space><span>{record.id}</span><span><CheckCircleOutlined
                style={record.verified === 1 ? {color: 'green'} : {color: "red"}}/></span></Space>,
        },
        {
            title: 'Barcode',
            dataIndex: ["barcode", "0", "barcode"],
            key: 'barcode',
            width: 30,
        },
        {
            title: 'logo_image',
            dataIndex: 'logo_image',
            key: 'logo_image',
            width: 30,
            render: img => (<img style={{width: 50}} src={img} alt=""/>)
        },
        {
            title: 'Name',
            dataIndex: 'original_name',
            key: 'original_name',
            render: (name, record) => {
                return (
                    <Link to={"/ingredient/" + record[idIndex]}>{name}</Link>
                )
            },
            width: 200,
        },
        {
            title: 'Energy (kcal)',
            dataIndex: 'energy',
            key: 'energy',
            width: 30,
        },

        {
            title: 'Carbohydrate (g)',
            dataIndex: 'carbohydrate',
            key: 'carbohydrate',
            width: 30,
        },
        {
            title: 'Protein (g)',
            dataIndex: 'protein',
            key: 'protein',
            width: 30,
        },
        {
            title: 'Fat (g)',
            dataIndex: 'fat',
            key: 'fat',
            width: 30,
        },
        {
            title: 'Fiber (g)',
            dataIndex: 'fiber',
            key: 'fiber',
            width: 30,
        },

        {
            title: 'User ID',
            dataIndex: 'user_id',
            key: 'user_id',
            width: 30,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            hidden: active === 'history' ? true : false,
            render: text => <span><Tag color={text === 1 ? "green" : "red"}>{text === 1 ? "active" : "inactive"}</Tag></span>,
        },
        {
            title: 'Score',
            dataIndex: 'score',
            key: 'score',
            render: (score, record) => {
                if (editingScore.id === record.id) {
                    return (
                        <Space>
                            <InputNumber
                                value={editingScore.value ?? score}
                                min={0}
                                max={10}
                                onChange={(val) =>
                                    setEditingScore({id: record.id, value: val})
                                }
                            />
                            <Button onClick={() =>
                                saveIngredientScore(record.id, editingScore.value ?? score)
                            } icon={<CheckOutlined/>}
                            />
                        </Space>
                    );
                }
                return (
                    <span onClick={() => setEditingScore({id: record.id, value: score})}>
        {score !== undefined ? score : "-"}{" "}
                        <span style={{opacity: 0.4}}>(click to edit)</span>
      </span>
                );
            },
            width: 100,
        }
        ,
        {
            title: "Action",
            key: "action",
            hidden: active === 'history',
            render: (text, record) =>
                <Space size="middle">
                    <Popconfirm
                        title={`Verify macros for ${record.original_name}`}
                        onConfirm={() => verifyIngredientMacros(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <a style={record.verified === 1 ? {display: "none"} : null}>
                            <CheckOutlined/> check
                        </a>
                    </Popconfirm>
                    <Popconfirm
                        title={`Are you sure you want to delete: ${record.original_name}?`}
                        onConfirm={() => deleteIngredient(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <a key="delete-comment" className="delete-comment"><DeleteOutlined/> delete</a>
                    </Popconfirm>
                </Space>,
            width: 30,
        },
    ];

    const loadIngredients = (nextPage, keywords) => {
        let params = `&per_page=${pageSize}`;
        if (keywords || search) {
            if (keywords && keywords !== search) {
                params += `&keywords=${keywords}`;
            } else {
                params += `&keywords=${!search ? keywords : search}`;
            }
        }
        if (nextPage) {
            params += `&page=${nextPage}`;
        } else {
            params += '&page=1';
        }
        params += '&order_by=id';

        Api.get('/admin/ingredients?type=' + active + params).then(response => {
            setPage(response.data.current_page);
            setMeta(response.data);
            setIngredients(response.data.data.map(i => ({...i, score: i.score || null})));
        });
    };

    const onSearch = (value) => {
        setSearch(value);
        loadIngredients(null, value);
    };

    const onChange = (data) => {
        setPageSize(data.pageSize);
        loadIngredients(data.current);
    };

    const changeTab = (value) => {
        setActive(value);
    };

    const deleteIngredient = (ingredient) => {
        Api.delete('/admin/ingredients/' + ingredient).then(res => {
            loadIngredients();
            message.success('Ingredient deleted');
        });
    };

    const verifyIngredientMacros = (ingredient) => {
        Api.get('/admin/ingredients/' + ingredient + '/verify').then(res => {
            if (res.data === 1) {
                message.success('Ingredient macros are in spec');
            } else {
                message.error('Ingredient macros are out of spec');
            }
            loadIngredients();
        });
    };

    return (
        <Content style={{margin: '0 16px'}}>
            <Breadcrumb style={{margin: '16px 0'}}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>Ingredients</Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <h1>
                    Ingredients ({meta.total})
                    <Search placeholder="search" onSearch={onSearch}
                            style={{width: 200, float: "right", marginRight: 10}}/>
                </h1>

                <Space style={{marginBottom: 20}}>
                    <NewBarcodeIngredient/>

                    <NewIngredient loadIngredients={loadIngredients}/>

                    <Button
                        icon={<RobotOutlined/>}
                        type="primary"
                        onClick={generateIngredientScores}
                        style={{
                            background: "linear-gradient(90deg, #ff34ff, rgb(255 95 95))",
                            border: "none",
                            color: "white",
                            fontWeight: "bold"
                        }}
                    >
                        AI Generate Scores
                    </Button>

                </Space>

                <Tabs defaultActiveKey="1" onChange={changeTab}>
                    <TabPane tab="Fitlap" key="fitlap">
                        <Table pagination={{pageSize: pageSize, total: meta.total, current: meta.current_page}}
                               onChange={onChange} columns={columns} dataSource={ingredients} size="small"/>
                    </TabPane>
                    <TabPane tab="Barcodes" key="user">
                        <Table pagination={{pageSize: pageSize, total: meta.total, current: meta.current_page}}
                               onChange={onChange} columns={columns} dataSource={ingredients} size="small"/>
                    </TabPane>
                    <TabPane tab={<div>Barcodes <Tag color="red">Unverified</Tag></div>} key="user_unverified">
                        <Table pagination={{pageSize: pageSize, total: meta.total, current: meta.current_page}}
                               onChange={onChange} columns={columns} dataSource={ingredients} size="small"/>
                    </TabPane>
                    <TabPane tab="History" key="history">
                        <Table pagination={{pageSize: pageSize, total: meta.total, current: meta.current_page}}
                               onChange={onChange} columns={columns} dataSource={ingredients} size="small"/>
                    </TabPane>
                </Tabs>
            </div>
        </Content>
    );
};

export default Ingredients;
